import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import '../styles/AllBookings.css';

const AllBookings = () => {
  const [token, setToken] = useState(null);
  const [groupedBookings, setGroupedBookings] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState("Confirmed");
  const [attachments, setAttachments] = useState({}); // store file per bookingId
  const navigate = useNavigate();

  useEffect(() => {
    const storedToken = localStorage.getItem('admin-auth-token');
    if (!storedToken) {
      navigate('/admin_login_page');
    } else {
      setToken(storedToken);
    }
  }, [navigate]);

  useEffect(() => {
    if (token) {
      fetchBookings();
    }
    // eslint-disable-next-line
  }, [token, status]);

  const fetchBookings = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL_SCHEME}${process.env.REACT_APP_IP_ADDRESS}/api/fetchBookings`, {
        method: "POST",
        headers: {
          'Content-type': 'application/json',
          'auth-token': token,
        },
        body : JSON.stringify({status})
      });

      const data = await response.json();
      if (Array.isArray(data)) {
        const sorted = data.sort((a, b) => new Date(a.checkInDate) - new Date(b.checkInDate));
        const grouped = sorted.reduce((acc, booking) => {
          const dateKey = new Date(booking.checkInDate).toLocaleDateString();
          acc[dateKey] = acc[dateKey] || [];
          acc[dateKey].push(booking);
          return acc;
        }, {});
        setGroupedBookings(grouped);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteBooking = async (_id) => {
  try {
    const confirmDelete = window.confirm('Do you want to delete the booking permanently.')
    if(!confirmDelete) return

    const response = await fetch(
      `${process.env.REACT_APP_URL_SCHEME}${process.env.REACT_APP_IP_ADDRESS}/api/deleteBooking`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          "auth-token": token,   // 🔑 add this if API is protected
        },
        body: JSON.stringify({ _id }),
      }
    );

    const result = await response.json();

    if (response.ok) {
      // Remove deleted booking from state without re-fetching everything
      setGroupedBookings(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(date => {
          updated[date] = updated[date].filter(b => b._id !== _id);
          if (updated[date].length === 0) {
            delete updated[date]; // remove empty group
          }
        });
        return updated;
      });
      alert(result.message);
    } else {
      alert(result.message || "Failed to delete booking");
    }
  } catch (error) {
    console.error("Error deleting booking:", error);
    alert("Something went wrong while deleting booking");
  }
};

 const handleFileChange = (bookingId, file) => {
    setAttachments(prev => ({ ...prev, [bookingId]: file }));
  };

  const handleRefund = async (booking) => {
  const confirmRefund = window.confirm("Do you want to mark this booking as refunded?");
  if (!confirmRefund) return;

  const formData = new FormData();
  formData.append("email", booking.email);
  formData.append("name", booking.guest?.[0]?.name || "Guest");
  formData.append("amount", booking.refundAmount);
  formData.append("razorpay_order_id", booking.razorpay_order_id);

  if (attachments[booking._id]) {
    formData.append("attachment", attachments[booking._id]);
  }

  try {
    const response = await fetch(
      `${process.env.REACT_APP_URL_SCHEME}${process.env.REACT_APP_IP_ADDRESS}/api/send-refund`,
      {
        method: "POST",
        body: formData
      }
    );

    const result = await response.json();

    if (response.ok) {
      alert("Refund mail sent successfully!");
      // update status locally
      setGroupedBookings(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(date => {
          updated[date] = updated[date].map(b =>
            b._id === booking._id ? { ...b, status: "Refunded" } : b
          );
        });
        return updated;
      });
    } else {
      alert(result.message || "Failed to send refund email");
    }
  } catch (error) {
    console.error("Refund error:", error);
    alert("Something went wrong");
  }
};


  const downloadPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(18);
    doc.setTextColor(40);
    doc.text(`All Bookings Report`, 14, 20);
    doc.setFontSize(10);

    let startY = 30;

    Object.entries(groupedBookings).forEach(([date, bookings]) => {
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(`Check-In Date: ${date}`, 14, startY);
      startY += 5;

      const tableData = bookings.map(booking => {
        const guests = booking.guest?.map(g => 
          `Name: ${g.name}\nAge: ${g.age}\nAadhar: ${g.aadhar}`
        ).join('\n') || 'No guests';

        const address = [
          booking.address.country,
          booking.address.state,
          booking.address.district,
          booking.address.city,
          `Pincode: ${booking.address.pincode}`
        ].filter(Boolean).join('\n');

        return [
          `${booking.type} [ ${booking.numberOfRooms} room ]`,
          guests,
          address,
          `${booking.mobileNumber}\n${booking.email}`,
          `Check-Out: ${new Date(booking.checkOutDate).toLocaleDateString()}`,
          `Paid: ${booking.paidAmount}\nPending: ${booking.pendingAmount}`,
          booking.razorpay_order_id || 'N/A',
          booking.status
        ];
      });

      autoTable(doc, {
        startY,
        head: [[
          'Room', 'Guest Details', 'Address', 'Contact', 'Dates',
          'Payment', 'Order ID', 'Status'
        ]],
        body: tableData,
        styles: { fontSize: 6, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 35 }, 1: { cellWidth: 45 }, 2: { cellWidth: 40 },
          3: { cellWidth: 35 }, 4: { cellWidth: 30 }, 5: { cellWidth: 30 },
          6: { cellWidth: 35 }, 7: { cellWidth: 20 }
        },
        theme: 'grid',
      });

      startY = doc.lastAutoTable.finalY + 10;
    });

    doc.save('all_bookings.pdf');
  };

  return (
    <div className="bookings-container">
      <div className="bookings-header">
        <h2>Room Bookings</h2>
      </div>
      <div className="button-container">
          <button className='booking-status status-confirmed' onClick={()=> setStatus("Confirmed")}>Confirmed</button>
          <button className='booking-status status-cancelled' onClick={()=> setStatus("Cancelled")}>Cancelled</button>
          <button className='booking-status status-refunded' onClick={()=> setStatus("Refunded")}>Refunded</button>
          <button className='booking-status ' onClick={()=> setStatus("Processing")}>Refund Req</button>
      </div>

      {isLoading ? (
        <div className="spinner"></div>
      ) : Object.keys(groupedBookings).length > 0 > 0 ?(
        <div>
          {Object.entries(groupedBookings).map(([date, bookings]) => (
            <div key={date} className='date-heading'>
              <h3 style={{ textAlign: 'center', marginTop: '30px', color: '#444' }}>
                Check-In : {date}
              </h3>
              <div className="bookings-grid">
                {bookings.map((booking) => (
                  <div className="booking-card" key={booking._id}>
                  {booking.status !== "Processing" ? <button className='close-booking-card' onClick={()=> deleteBooking(booking._id)}>x</button> : null}
                    <div className="card-body">
                      <h1 className="card-header">{booking.type} Room</h1>
                      <p className="detail-label">Number Of Rooms: {booking.numberOfRooms}</p>
                      <p className="detail-label">Number Of Guests: {booking.numberOfGuest}</p>
                      {booking.guest?.map((guest, index) => (
                        <div className="guest-detail" key={index}>
                          <p className="detail-label">Guest {index + 1} : {guest.name}</p>
                          <p className="detail-label">Age : {guest.age}</p>
                          <p className="detail-label">Aadhar : {guest.aadhar}</p>
                        </div>
                      ))}
                      <div className="guest-detail">
                        <p className="detail-label">Country : {booking.address.country}</p>
                        <p className="detail-label">State : {booking.address.state}</p>
                        <p className="detail-label">District : {booking.address.district}</p>
                        <p className="detail-label">City : {booking.address.city}</p>
                        <p className="detail-label">Pincode : {booking.address.pincode}</p>
                      </div>
                      <p className="detail-label">Mobile Number : {booking.mobileNumber}</p>
                      <p className="detail-label">Email : {booking.email}</p>
                      <p className="detail-label">Amount Paid : {booking.paidAmount}</p>
                      <p className="detail-label">Amount Pending : {booking.pendingAmount}</p>
                      <p className="detail-label">Order ID : {booking.razorpay_order_id}</p>
                      {booking.refundId ? <p className="detail-label">Refund ID : {booking.refundId}</p> : ""}
                      {booking.refundAmount ? <p className="detail-label">Refund Amount : {booking.refundAmount}</p> : ""}
                      <p className="detail-label">Check-Out : {new Date(booking.checkOutDate).toLocaleDateString()}</p>
                      <p className={`detail-label booking-status status-${booking.status.toLowerCase()}`}>{booking.status}</p>
                      {booking.status === "Processing" ? (
                        <>
                        <input type='file' onChange={(e) => handleFileChange(booking._id, e.target.files[0])}/>
                        <button className="download-pdf-btn" onClick={() => handleRefund(booking)}>Refunded</button>
                        </>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : <p className='no-booking'>No bookings in this category.</p>}

      {Object.keys(groupedBookings).length > 0 && status === "Confirmed" &&(
        <button onClick={downloadPDF} className="download-pdf-btn">Download as PDF</button>
      )}
    </div>
  );
};

export default AllBookings;
